import * as XLSX from 'xlsx'
import { ComplianceStatus } from '../../../types'
import { getCorpChildrenTrainings, getCorpTrainingByYear } from './services'
import {
  CorpExportCustomer,
  CorpTrainingByYearRow,
  flattenCorpUserTrainings,
  formatByYearRow,
  getAllTrainingRow,
  getCustomerHeader,
  getCustomersById,
  getTrainingByPracticeRow,
  getTrainingRowsByName,
  sortByYearRows,
} from './utils'

export type { CorpTrainingByYearRow } from './utils'

export const exportAllTraining = async (customerNumber: string) => {
  const customers: CorpExportCustomer[] = await getCorpChildrenTrainings(customerNumber)
  const userTrainings = flattenCorpUserTrainings(customers)

  if (!userTrainings.length) {
    return false
  }

  const childrenById = getCustomersById(customers)
  const data = userTrainings.map((userTraining) => getAllTrainingRow(userTraining, childrenById))
  const rowsByName = getTrainingRowsByName(userTrainings, childrenById)
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'All Training')

  const sortedCourseNames = [...rowsByName.keys()].sort()
  for (const course of sortedCourseNames) {
    const courseSheet = XLSX.utils.json_to_sheet(rowsByName.get(course)!)
    XLSX.utils.book_append_sheet(wb, courseSheet, course.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 15))
  }

  XLSX.writeFile(wb, 'All_Training.xlsx')
  return true
}

export const exportTrainingByPractice = async (customerNumber: string) => {
  const customers: CorpExportCustomer[] = await getCorpChildrenTrainings(customerNumber)
  const wb = XLSX.utils.book_new()
  let customerTrainingCount = 0

  customers.forEach((customer) => {
    if (!customer.users_training?.length) {
      return
    }

    customerTrainingCount++
    const ws = XLSX.utils.json_to_sheet(getCustomerHeader(customer), { skipHeader: true })
    const customerTrainings = customer.users_training
      .filter((userTraining) => userTraining.compliance_status !== ComplianceStatus.NotApplicable)
      .map((userTraining) => getTrainingByPracticeRow({
        ...userTraining,
        customer_number: customer.customer_number,
      }))

    XLSX.utils.sheet_add_json(ws, customerTrainings, { origin: -1 })

    const sheetName = `${customer.name.trim().slice(0, 13)}_${customer.customer_number}`
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
  })

  if (!customerTrainingCount) {
    return false
  }

  XLSX.writeFile(wb, 'Training_By_Practice.xlsx')
  return true
}

export const exportTrainingByYear = async (customerNumber: string) => {
  const trainings: CorpTrainingByYearRow[] = await getCorpTrainingByYear(customerNumber)

  if (!trainings?.length) {
    return false
  }

  const trainingsByYear = trainings.reduce<Record<string, CorpTrainingByYearRow[]>>((acc, row) => {
    const year = String(row.year)
    if (!acc[year]) {
      acc[year] = []
    }
    acc[year].push(row)
    return acc
  }, {})

  const years = Object.keys(trainingsByYear).sort((a, b) => Number(a) - Number(b))
  const wb = XLSX.utils.book_new()

  years.forEach((year) => {
    const formattedRows = sortByYearRows(trainingsByYear[year]).map(formatByYearRow)
    const ws = XLSX.utils.json_to_sheet(formattedRows)
    XLSX.utils.book_append_sheet(wb, ws, year)
  })

  XLSX.writeFile(wb, 'Training_By_Year.xlsx')
  return true
}
